// hooks/useNotifications.js - Custom hook for managing notifications

import { useState, useEffect, useCallback, useRef } from 'react';
import notificationService from '../services/notificationService';

const isDevelopment = process.env.NODE_ENV !== 'production';

const getTestNotification = () => ({
  id: 'test-notification',
  type: 'ticket_assigned',
  title: 'New ticket assigned',
  message: ' New ticket assigned: INC1234',
  timestamp: new Date().toISOString(),
  read: false,
  isTest: true
});

export const useNotifications = (userId) => {
  const [notifications, setNotifications] = useState(() => (isDevelopment ? [getTestNotification()] : []));
  const [unreadCount, setUnreadCount] = useState(() => (isDevelopment ? 1 : 0));
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected, connecting, connected
  const notificationsRef = useRef([]);

  /**
   * Handle new notification received
   */
  const handleNotificationReceived = useCallback((notification) => {
    // Skip heartbeat messages and connection confirmations
    if (notification.type === 'heartbeat' || notification.message === 'Notification stream connected') {
      return;
    }

    setNotifications(prev => {
      const realNotifications = prev.filter(n => !n.isTest);
      // Keep only last 10 notifications (as per user preference)
      const updated = [notification, ...realNotifications].slice(0, 10);
      notificationsRef.current = updated;
      
      // Update unread count
      const unread = updated.filter(n => !n.read).length;
      setUnreadCount(unread);
      
      return updated;
    });

    // Optional: Show browser notification
    if (Notification.permission === 'granted') {
      showBrowserNotification(notification);
    }
  }, []);

  /**
   * Handle connection error
   */
  const handleError = useCallback((error) => {
    console.error('[useNotifications] Error:', error);
    setConnectionStatus('disconnected');
  }, []);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => {
      const updated = prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      );
      const unread = updated.filter(n => !n.read).length;
      setUnreadCount(unread);
      return updated;
    });
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true }));
      setUnreadCount(0);
      return updated;
    });
  }, []);

  /**
   * Clear all notifications
   */
  const clearNotifications = useCallback(() => {
    if (isDevelopment) {
      setNotifications([getTestNotification()]);
      setUnreadCount(1);
      return;
    }

    setNotifications([]);
    setUnreadCount(0);
  }, []);

  /**
   * Show browser notification
   */
  const showBrowserNotification = (notification) => {
    const title = notification.title || 'Agent Assist';
    const options = {
      body: notification.message,
      icon: notification.icon || '/favicon.ico',
      tag: notification.id,
      requireInteraction: notification.priority === 'high'
    };
    new Notification(title, options);
  };

  /**
   * Request browser notification permission
   */
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }, []);

  // Connect to notification stream on mount
  useEffect(() => {
    if (!userId) return;

    setConnectionStatus('connecting');
    notificationService.connect(
      userId,
      handleNotificationReceived,
      handleError
    );

    // Update connection status after a delay
    const statusTimer = setTimeout(() => {
      if (notificationService.isConnected()) {
        setConnectionStatus('connected');
      }
    }, 1000);

    return () => {
      clearTimeout(statusTimer);
      // Don't disconnect on unmount - keep listening in background
    };
  }, [userId, handleNotificationReceived, handleError]);

  return {
    notifications,
    unreadCount,
    connectionStatus,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    requestNotificationPermission
  };
};

export default useNotifications;
